import { Dtos, SuretyAccountId, SuretyAccountUnderwritingId } from "@inrev/common";
import { useAdminUpdateUnderwritingExpiration } from "@portal/api/account/admin";
import { FormDatePicker } from "@portal/components/form/fields";
import { LoadingModal } from "@portal/components/layout/LoadingModal";
import { Modal } from "@portal/components/layout/Modal";
import { ModalItemWithHeader } from "@portal/components/layout/ModalItemWithHeader";
import { Button, Text } from "@portal/components/primitives";
import { DateTime } from "luxon";
import { FC, useState } from "react";
import { useForm } from "react-hook-form";
import { typedZodResolver } from "../../../../../utils/form";

export type AdminUpdateUnderwritingExpirationModalProps = {
	expiresAt: DateTime<true> | null | undefined;
	accountId: SuretyAccountId;
	underwritingId: SuretyAccountUnderwritingId;
};

export type UpdateUnderwritingExpirationModalProps = AdminUpdateUnderwritingExpirationModalProps & {
	close: () => void;
};

export type AdminUpdateUnderwritingExpirationFormData = {
	expiresAt: DateTime<true> | null | undefined;
};

export const AdminUpdateUnderwritingExpirationModal: FC<
	AdminUpdateUnderwritingExpirationModalProps
> = ({ expiresAt, accountId, underwritingId }) => {
	const [open, setOpen] = useState(false);

	return (
		<div>
			<span className="cursor-pointer hover:underline" onClick={() => setOpen(true)}>
				{expiresAt ? expiresAt.toLocaleString(DateTime.DATE_SHORT) : "--"}
			</span>
			{open && (
				<UpdateUnderwritingExpirationModal
					accountId={accountId}
					underwritingId={underwritingId}
					close={() => setOpen(false)}
					expiresAt={expiresAt}
				/>
			)}
		</div>
	);
};

export const UpdateUnderwritingExpirationModal: FC<UpdateUnderwritingExpirationModalProps> = ({
	close,
	expiresAt,
	accountId,
	underwritingId,
}) => {
	const { updateUnderwritingExpiration, updateUnderwritingExpirationIsLoading } =
		useAdminUpdateUnderwritingExpiration(accountId, underwritingId);

	const formMethods = useForm<
		AdminUpdateUnderwritingExpirationFormData,
		any,
		Dtos.Admin.SuretyAccount.Update.UnderwritingExpiration.Request
	>({
		defaultValues: {
			expiresAt: expiresAt,
		},
		reValidateMode: "onBlur",
		mode: "onSubmit",
		resolver: typedZodResolver(
			Dtos.Admin.SuretyAccount.Update.UnderwritingExpiration.Request.schema,
		),
	});

	return (
		<Modal onClickOutside={close}>
			<ModalItemWithHeader
				header={
					<Text type="title" color="primary">
						Underwriting Expiration
					</Text>
				}
				onClose={close}
				className="h-fit overflow-hidden"
				bodyClassName="w-full overflow-y-auto"
			>
				<div
					className="flex flex-col space-y-4 pt-[25px] px-[30px] pb-[30px]"
					data-testid="update-underwriting-expiration-modal"
				>
					<FormDatePicker
						control={formMethods.control}
						name="expiresAt"
						disableBefore={DateTime.now().plus({ days: 1 }).toISODate()}
						defaultMonth={expiresAt ?? DateTime.now()}
						errorMessage={formMethods.formState.errors.expiresAt?.message}
					/>
					<div className="flex justify-between space-x-2 mt-4">
						<Button
							color="danger"
							onClick={() => {
								formMethods.handleSubmit(() => {
									const requestData: Dtos.Admin.SuretyAccount.Update.UnderwritingExpiration.Request =
										{
											expiresAt: null,
										};
									updateUnderwritingExpiration(requestData);
									close();
								})();
							}}
						>
							Reset to default
						</Button>
						<Button
							color="primary"
							onClick={() => {
								formMethods.handleSubmit((data) => {
									const requestData: Dtos.Admin.SuretyAccount.Update.UnderwritingExpiration.Request =
										{
											expiresAt: data.expiresAt,
										};
									updateUnderwritingExpiration(requestData);
									close();
								})();
							}}
						>
							Save
						</Button>
					</div>
				</div>
			</ModalItemWithHeader>
			{updateUnderwritingExpirationIsLoading && <LoadingModal />}
		</Modal>
	);
};
